<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link, useNavigate } from 'svelte-navigator';
  import {
    Breadcrumb,
    BreadcrumbItem,
    Button,
    ButtonDropdown,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row
  } from '@sveltestrap/sveltestrap';
  import { createQuery } from '@tanstack/svelte-query';

  import { FilterBar } from '@client/components/filter-bar';
  import { MultiSelectFilter } from '@client/components/filters/multiselect';
  import { SearchFilter } from '@client/components/filters/search';
  import { SortFilter } from '@client/components/filters/sort';
  import { GroupCard } from '@client/components/group-card';
  import { LayoutControls } from '@client/components/layout-controls';
  import { Loader } from '@client/components/loader';
  import { NoMatches } from '@client/components/no-matches';
  import { getNotificationsContext } from '@client/components/notifications';
  import { RenameModal } from '@client/components/rename-modal';
  import { Table } from '@client/components/table';

  import { GroupsService } from '@client/services/groups';
  import { SignaturesService } from '@client/services/signatures';

  import { appStore } from '@client/stores/app';
  import { groupsSettingsStore } from '@client/stores/page-settings';

  import { QueryParams } from '@client/utils/query-params';

  import { GROUP_FILTERS } from '@client/enums';

  import { deepCopyArray } from '@common/utils/arrays';
  import { sortCollection } from '@common/utils/arrays';
  import { generatePastelColors } from '@common/utils/colors';

  import { ActionsCell, AssigneesCell, BlocksCell, NameCell, StatusCell } from './cell-formatters';
  import { CreateGroupModal, DiagnosticsModal } from './modals';

  const { basepath } = getContext('config');
  const { addNotification } = getNotificationsContext();

  const queryParams = {};
  const navigate = useNavigate();

  let renderCardLayout = ($appStore.mobile || $groupsSettingsStore.cards) ?? true;

  let original = [];
  let filtered = [];
  let showFilters = true;
  let showCreateGroupModal = false;
  let showDiagnosticModal = false;
  let showRenameModal = false;
  let renameGroup = null;
  let initialized = false;
  let excludedNames = {};

  let filters = {
    ...GROUP_FILTERS,
    groups: {},
    search: QueryParams.get('search'),
    sorting: {
      dir: QueryParams.get('sortDir') || 'asc',
      key: QueryParams.get('sortKey') || 'status',
      options: {
        ...GROUP_FILTERS.sorting.options
      }
    }
  };

  // Excluded bindings arrive from sendergroups as bare { id } with no name.
  // Resolve their display names/counts the same way the edit form's
  // group-selector does — by calling the provider endpoints — but once per
  // provider for the whole page, not per card. Non-blocking: the cards render
  // immediately and the names fill in when this resolves.
  const PROVIDER_METHODS = {
    googlegroupbindings: 'getGoogleGroups',
    o365groupbindings: 'getOfficeGroups',
    oktagroupbindings: 'getOktaGroups',
    googleorgbindings: 'getGoogleOrgs',
    csvtreenodebindings: 'getWorkdayManagers'
  };

  const hydrateExcludedNames = async (groups) => {
    const byProvider = {};

    for (const group of groups) {
      for (const field of Object.keys(PROVIDER_METHODS)) {
        const excluded = group.exclusions?.[field];
        if (excluded?.length) {
          byProvider[field] = (byProvider[field] || []).concat(excluded);
        }
      }
    }

    const map = {};

    await Promise.all(
      Object.entries(byProvider).map(async ([field, bindings]) => {
        try {
          const list = await GroupsService[PROVIDER_METHODS[field]]({ bindings, query: '' });
          if (!Array.isArray(list)) return;

          for (const item of list) {
            if (field === 'csvtreenodebindings') {
              if (item.employee_id) {
                map[item.employee_id] = {
                  name: `${item.first ?? ''} ${item.last ?? ''}`.trim(),
                  count: item.total_reports
                };
              }
            } else if (item.uuid) {
              map[item.uuid] = { name: item.name ?? item.displayName ?? '', count: item.directMembersCount };
            }
          }
        } catch (e) {
          // Leave unresolved excluded names blank; the card degrades gracefully.
        }
      })
    );

    excludedNames = map;
  };

  const queryFn = async () => {
    const response = await GroupsService.getAll();
    const signature = await SignaturesService.getAll();
    const colors = generatePastelColors(response.length);

    initialized = true;

    const data = response
      .map((group, index) => {
        const config = {
          ...group,
          color: colors[index],
          senders: group.senders || []
        };

        const filter = signature.filter(
          ({ sendergroups }) => sendergroups.filter(({ groupname }) => groupname === group.groupname).length
        );

        if (filter.length) {
          const primary = filter.find(({ blockname, status }) => blockname && status) || filter.shift();
          config.blockname = primary.blockname;
          config.status = primary.status;
          config.sigparams = primary.tagblock?.params || [];
          config.blocks = filter;
        } else {
          config.blocks = [];
          config.blockname = null;
          config.status = false;
        }

        return config;
      })
      .sort((a, b) => a.groupname.localeCompare(b.groupname));

    original = filtered = data;

    update();

    hydrateExcludedNames(data);

    return data;
  };

  const query = createQuery({
    queryKey: ['groups', queryParams],
    queryFn
  });

  const update = () => {
    const selectedStatus = Object.values(filters.status)
      .map(({ enabled, value }) => enabled && value)
      .filter(Boolean);

    filtered = deepCopyArray(original);

    if (filters.search) {
      filtered = filtered.filter((row) => {
        const search = filters.search.toLowerCase();

        return Object.values(row).some((field) => {
          if (typeof field === 'string') {
            return field.toLowerCase().includes(search);
          }

          return false;
        });
      });
    }

    if (selectedStatus.length) {
      filtered = filtered.filter(({ status }) => selectedStatus.includes(status === true ? 'active' : 'inactive'));
    }

    filtered = sortCollection(filtered, filters.sorting.key, filters.sorting.dir === 'asc' ? -1 : 1);
  };

  const onRenameUpdate = async (group, newName) => {
    const response = await GroupsService.updateGroup({ ...group, groupname: newName });

    if (response?.status >= 400) {
      return addNotification({
        text: $format('label.FAILURE.GROUP_RENAME', { values: { group: group.groupname } }),
        type: 'error',
        autohide: 2500,
        position: 'top-right'
      });
    }

    addNotification({
      text: $format('label.SUCCESS.RENAME', { values: { from: group.groupname, to: newName } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });

    showRenameModal = false;
    queryFn();
  };

  const onSortChange = (selected, key, dir) => {
    filters.sorting = { ...filters.sorting, key, dir };
  };

  const onChangeLayout = (state) => {
    renderCardLayout = state;
    $groupsSettingsStore.cards = state;
  };

  let columns = [
    {
      key: 'groupname',
      title: $format('label.NAME'),
      value: ({ groupname }) => groupname,
      renderComponent: NameCell,
      headerClass: 'name-cell',
      sortable: true
    },
    {
      key: 'senders',
      title: $format('label.USERS'),
      value: ({ senders }) => senders.join(','),
      renderComponent: AssigneesCell,
      headerClass: 'assignees-cell'
    },
    {
      key: 'blockname',
      title: $format('label.SIGNATURE'),
      value: ({ blockname }) => blockname,
      renderComponent: BlocksCell,
      headerClass: 'signatures-cell'
    },
    {
      key: 'status',
      title: $format('label.STATUS'),
      value: ({ status }) => !!status,
      renderComponent: StatusCell,
      headerClass: 'status-cell',
      sortable: true
    },
    {
      key: 'actions',
      title: '',
      value: '',
      headerClass: 'actions-cell',
      renderComponent: {
        component: ActionsCell,
        props: {
          onDelete: () => {
            queryFn();
          },
          onRename: (row) => {
            renameGroup = row;
            showRenameModal = true;
          }
        }
      }
    }
  ];

  $: update(filters);
</script>

<div id="groups" class="page" class:with-footer={$query.data?.data}>
  <Loader backdrop show={$query.isLoading || $query.isFetching} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/manage" use:link>{$format('label.MANAGE')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.GROUPS')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col xs="6" sm="6" md="6">
          <h1 class="page-title">{$format('label.MANAGE_GROUPS')}</h1>
        </Col>
        <Col xs="6" sm="6" md="6" class="text-right">
          <ButtonDropdown class="group-actions hide--mobile me-2">
            <DropdownToggle color="secondary" caret>{$format('label.RESOURCES')}</DropdownToggle>
            <DropdownMenu>
              <DropdownItem on:click={() => (showDiagnosticModal = true)}>
                {$format('label.GROUP_DIAGNOSTICS')}
              </DropdownItem>
            </DropdownMenu>
          </ButtonDropdown>

          <Button color="secondary" on:click={() => (showCreateGroupModal = true)}>{$format('label.NEW_GROUP')}</Button>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    <div class="toolbar">
      <div class="filters">
        <FilterBar bind:show={showFilters}>
          <SearchFilter bind:search={filters.search} />
          <MultiSelectFilter
            bind:filter={filters.status}
            param="status"
            label={$format('label.STATUS')}
            tooltipLabel={$format('label.SELECTED_STATUS')}
          />
          <SortFilter
            bind:filter={filters.sorting.options}
            bind:sortDirection={filters.sorting.dir}
            onClick={onSortChange}
            sortDirParam="sortDir"
            sortKeyParam="sortKey"
          />
          <LayoutControls {renderCardLayout} onChange={onChangeLayout} />
        </FilterBar>
      </div>
    </div>

    {#if $appStore.mobile || renderCardLayout}
      <div class="groups-layout">
        <div class="cards">
          {#if filtered.length}
            <div class="group-cards">
              {#each filtered as data}
                <GroupCard
                  {data}
                  {excludedNames}
                  refetch={queryFn}
                  onRename={(group) => {
                    renameGroup = group;
                    showRenameModal = true;
                  }}
                />
              {/each}
            </div>
          {:else if initialized}
            <NoMatches message={$format('label.MESSAGE.NO_MATCHES_GROUPS')} />
          {/if}
        </div>
      </div>
    {:else}
      <Table
        id="groups-table"
        classNameThead="sticky-header"
        emptyLabel={$format('label.MESSAGE.NO_MATCHES_GROUPS')}
        bind:rows={filtered}
        bind:columns
        bind:sortBy={filters.sorting.key}
        bind:sortOrder={filters.sorting.dir}
        on:clickRow={({ detail }) => navigate(`/manage/groups/${detail.row.groupname}`)}
      />
    {/if}
  </div>
</div>

<CreateGroupModal bind:show={showCreateGroupModal} />

<DiagnosticsModal bind:show={showDiagnosticModal} />

<RenameModal
  bind:show={showRenameModal}
  data={renameGroup}
  name={renameGroup?.groupname ?? ''}
  existingNames={original.map((g) => g.groupname)}
  duplicateError={$format('label.RENAME_MODAL.ERROR.DUPLICATE_GROUP')}
  onUpdate={onRenameUpdate}
/>

<style lang="scss" src="./groups.scss"></style>
